import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CaseAutocomplete, { CaseAutocompleteOption } from '../Common/CaseAutocomplete';

interface AssignDocumentToCaseDialogProps {
  open: boolean;
  onClose: () => void;
  documentTitle?: string;
  /** Matter the document is filed to today, if any. */
  currentCaseId?: string | null;
  currentCaseNumber?: string | null;
  saving?: boolean;
  onAssign: (caseId: string | null) => void;
}

const AssignDocumentToCaseDialog: React.FC<AssignDocumentToCaseDialogProps> = ({
  open,
  onClose,
  documentTitle,
  currentCaseId = null,
  currentCaseNumber = null,
  saving = false,
  onAssign,
}) => {
  const [selectedCase, setSelectedCase] = useState<CaseAutocompleteOption | string | null>(null);

  useEffect(() => {
    if (open) setSelectedCase(currentCaseId || null);
  }, [open, currentCaseId]);

  const selectedId =
    typeof selectedCase === 'string' ? selectedCase : selectedCase?.id || null;
  const unchanged = (selectedId || null) === (currentCaseId || null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Document to a Case</DialogTitle>
      <DialogContent>
        {documentTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {documentTitle}
          </Typography>
        )}
        <CaseAutocomplete
          value={selectedCase}
          onChange={setSelectedCase}
          label="Case"
          placeholder="Search by case number, title or client"
          size="small"
        />
        {currentCaseId && !selectedId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Clearing the case unfiles this document. It stays in the firm library and can be
            assigned again later.
          </Alert>
        )}
        {currentCaseId && selectedId && !unchanged && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This moves the document out of {currentCaseNumber || 'its current matter'}. If the new
            matter belongs to a different client, the document transfers to that client and leaves
            the previous client&apos;s portal.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onAssign(selectedId)}
          disabled={saving || unchanged}
        >
          {selectedId ? 'File to Case' : 'Remove from Case'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDocumentToCaseDialog;
